import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  Filter, 
  Check, 
  AlertCircle, 
  RefreshCw, 
  XCircle,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import { FaTimes } from 'react-icons/fa';
import ModernTimeDropdown from '../../layouts/modernDropdown.jsx';
import server from '../../../api/server.js';
import "../../../dashboard-components/loader.css"
import { useToast } from '../../../context/ToastContext.jsx';

const StatusPanel = ({isOpen, setIsOpen, clientID, serviceID}) => {
  const [timeFilter, setTimeFilter] = useState('Today');
  const [activeTab, setActiveTab] = useState('running');
  const [runningCount, setRunningCount] = useState(0);
  const [allCount, setAllCount] = useState(0);
  const [client_id, setClient_id] = useState(null);
  const [service_id, setService_id] = useState(null);
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showMoreMap, setShowMoreMap] = useState({});
  const [cancellingMap, setCancellingMap] = useState({});
  // Mock data for status cards
  const [statusItems, setStatusItems] = useState([]);
  const { showToast } = useToast();

  // Function to get icon and color for status
  const getStatusInfo = (status) => {
    switch(status) {
      case 'Processing':
        return { 
          icon: <RefreshCw className="h-5 w-5 text-blue-500 animate-spin" />,
          color: 'text-blue-500',
          bgColor: 'bg-blue-100' 
        };
      case 'Processed':
        return { 
          icon: <Check className="h-5 w-5 text-green-500" />,
          color: 'text-green-500',
          bgColor: 'bg-green-100' 
        };
      case 'Failed':
        return { 
          icon: <AlertCircle className="h-5 w-5 text-red-500" />,
          color: 'text-red-500',
          bgColor: 'bg-red-100' 
        };
      case 'Cancelled':
        return { 
          icon: <XCircle className="h-5 w-5 text-gray-500" />,
          color: 'text-gray-500',
          bgColor: 'bg-gray-100' 
        };
      default:
        return { 
          icon: <Clock className="h-5 w-5 text-gray-500" />,
          color: 'text-gray-500',
          bgColor: 'bg-gray-100' 
        };
    }
  };
  const getReadableTimeFromEpoch = (epochTime) => {
    const date = new Date(epochTime*1000);
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' };
    return date.toLocaleString('en-US', options).replace(' at ', ' ');
  };
  const toggleStatusPanel = () => {
    setIsOpen(!isOpen);
  };
  const handleCancelTask = async (item) => {
    try {
      if (!client_id || !service_id || !email) return;
      setCancellingMap(prev => ({...prev, [item.batch_linking_id]: true}));
      const payload = {
        client_id,
        service_id,
        email,
        menu: 'home',
        submenu: undefined,
        batch_linking_id: item.batch_linking_id,
        source_type: item.source_type,
      };
      let res = await server.update_task_status(payload);
      if (res.status === 200) {
        if (showToast) showToast('Cancel request sent.', 'success');
        await getTaskStatusList();
      } else {
        if (showToast) showToast('Failed to cancel task.', 'error');
      }
    } catch (e) {
      if (showToast) showToast('Something went wrong while cancelling.', 'error');
    } finally {
      setCancellingMap(prev => ({...prev, [item.batch_linking_id]: false}));
    }
  };
  const getTaskStatusList = async () => {
    try {
        setLoading(true);
        const res = await server.getTaskStatusList(client_id, service_id, email, 'home', undefined, timeFilter)
        const resp = await res.json();
        if(res.status === 200){
            let arr1 = []
            let arr2 = []
            let runningCount = 0
            for(let i = 0; i < resp.data.length; i++){
                if(resp.data[i].overall_status === 'processing' || resp.data[i].overall_status === 'queued'){
                    arr2.push(resp.data[i])
                    runningCount += 1
                }
                arr1.push(resp.data[i])
            }
            if(activeTab === 'running'){
                setRunningCount(runningCount)
                setStatusItems(arr2);
            }
            else{
                setStatusItems(arr1);
            }
        }
        setLoading(false);
    } catch (error) {
        setLoading(false);
    }
  }
    function getCookie(name) {
        function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
        var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
        return match ? match[1] : null;
    }
  useEffect(()=> {
    if(client_id && service_id && email){
        getTaskStatusList();
    }
  }, [client_id, service_id, email, timeFilter, activeTab])
  useEffect(() => {
    if(clientID){
        setClient_id(clientID);
    }
    if(serviceID){
        setService_id(serviceID);
    }
    const user = getCookie('dashboard_login_user_creds') ? JSON.parse(getCookie('dashboard_login_user_creds')) : null;
        if (user) {
            setEmail(user.email);
        }
  }, [clientID, serviceID]);
  

  return (
    <div className="bg-white rounded-lg shadow-lg w-full mx-auto">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-800">Task Status</h3>
              <button 
                onClick={toggleStatusPanel}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes style={{color: "slategray"}}/>
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              <button
                className={`flex-1 py-3 text-sm font-medium ${activeTab === 'running' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveTab('running')}
              >
                Running ({runningCount})
              </button>
              <button
                className={`flex-1 py-3 text-sm font-medium ${activeTab === 'all' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveTab('all')}
              >
                All
              </button>
            </div>
      {/* Header with time filters */}
      <div className="flex items-center justify-between border-b border-gray-200 p-1">
        <div style={{fontSize: '12px', color: '#4b5563', marginLeft: '8px'}}>Status updates once in 30 seconds</div>
        <div className="flex space-x-2 justify-end">
            <ModernTimeDropdown options={['Today','Yesterday',"Last 7 Days"]} value={timeFilter} onChange={(e) => setTimeFilter(e.target.value)}/>
        </div>
      </div>

      {/* Status cards */}
      <div className=" max-h-[80vh] overflow-y-auto">
        {statusItems.length === 0 && !loading ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <Clock className="h-12 w-12 text-gray-300 mb-3" />
            <p className="text-lg">{activeTab === 'running' ? 'No running items found' : 'No items found'}</p>
          </div>
        ) : loading ? (
            <div className="flex justify-center items-center" style={{ height: "80vh", flexDirection: "column" }}>
                <div className="loader"></div>
                <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
            </div>
        ) : (
          statusItems.map(item => {
            const statusInfo = getStatusInfo(item.overall_status);
            
            return (
              <div key={item.batch_linking_id} className="bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow m-1">
                <div className="p-2">
                  {/* Header: Time and Email */}
                  <div className="flex justify-between items-center mb-2">
                    <div className="text-xs text-gray-500 flex items-center">
                      {/* <Clock className="h-4 w-4 mr-1" /> */}
                      {getReadableTimeFromEpoch(item.createdat)}
                    </div>
                    <div className="text-xs text-gray-600">
                      {item.created_by}
                    </div>
                  </div>
                  
                  {/* Flow Type and Status */}
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center">
                      {/* {getFlowTypeIcon(item.flowType)} */}
                      <span className="text-sm">{item.source_type==='dashboard-run-apps' ? 'Run Apps' : (item.source_type==='dashboard-single-upload') ? 'Single Upload' : (item.source_type === 'dashboard-bulk-upload') ? 'Bulk Upload' : item.source_type}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {(['recieved','processing','queued'].includes(String(item.overall_status).toLowerCase())) && (
                        <button
                          onClick={() => handleCancelTask(item)}
                          disabled={!!cancellingMap[item.batch_linking_id]}
                          className={`px-2 py-1 text-xs rounded border ${cancellingMap[item.batch_linking_id] ? 'cursor-not-allowed opacity-60' : 'hover:bg-red-50'} border-red-300 text-red-600`}
                        >
                          {cancellingMap[item.batch_linking_id] ? 'Cancelling...' : 'Cancel'}
                        </button>
                      )}
                      <div className={`flex items-center px-3 py-1 rounded-full ${statusInfo.bgColor}`}>
                        {/* {statusInfo.icon} */}
                        <span className={`ml-1 text-xs font-medium ${statusInfo.color} capitalize`}>{item.overall_status}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="mb-2">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs font-medium text-gray-700">Progress</span>
                      <span className="text-xs text-gray-500">{item.total_request_processed} / {item.total_records}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1">
                      <div 
                        className={`h-1 rounded-full ${
                          item.overall_status === 'Failed' ? 'bg-red-500' : 
                          item.overall_status === 'Cancelled' ? 'bg-gray-500' : 
                          'bg-blue-500'
                        }`}
                        style={{ width: `${(item.total_request_processed / item.total_records) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  {/* Success/Failed counts */}
                  <div className="flex mt-3 text-sm">
                    <div className="flex items-center mr-6">
                      <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                      <span className="text-gray-600 text-xs">Success: <span className="font-medium">{item.total_success_count}</span></span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 rounded-full bg-red-500 mr-2"></div>
                      <span className="text-gray-600 text-xs">Failed: <span className="font-medium">{item.total_failed_count}</span></span>
                    </div>
                  </div>
                  {item.filters && item.filters !== null && item.filters !== undefined && item.filters !== '' && item.filters !== 'null' && item.filters !== 'undefined' && <div className='mt-2 bg-gray-100 rounded-lg p-2'>
                    <div className="text-gray-600 text-sm flex items-center justify-between" onClick={() => setShowMoreMap({...showMoreMap, [item.batch_linking_id]: !showMoreMap[item.batch_linking_id]})}>Applied Filters <span className='text-gray-400 text-xs cursor-pointer'>{showMoreMap[item.batch_linking_id] ? <ChevronUp className='h-4 w-4' /> : <ChevronDown className='h-4 w-4' />}</span></div>
                    {showMoreMap[item.batch_linking_id] && <div>
                    <div className="text-gray-600 text-xs flex items-center m-2"> 
                         <span className="font-medium flex items-center">
                            <span className='mr-1'>Date Range: </span> 
                            <span className='flex items-center px-1 py-1 rounded-full bg-white'>{JSON.parse(item.filters).date_range.start}
                            </span> - 
                            <span className='flex items-center px-1 py-1 rounded-full bg-white'>{JSON.parse(item.filters).date_range.end}
                            </span>
                        </span>
                    </div>
                    <div className="text-gray-600 text-xs flex flex-wrap items-center gap-1 m-2">
                      <span className="font-medium capitalize">Modules:</span>
                      {JSON.parse(item.filters).modules_list.map(module => module.replace('_', ' ')).map((module, index) => (
                        <span key={index} className='inline-flex items-center px-2 py-1 rounded-full bg-white whitespace-nowrap'>{module}</span>
                      ))}
                    </div>
                    <div className="text-gray-600 text-xs flex items-center m-2"> 
                        <span className="font-medium capitalize flex items-center gap-1 my-1">Processing Status: <span className='flex items-center px-1 py-1 rounded-full bg-white'>{JSON.parse(item.filters).processing_status}</span>
                        </span>
                    </div>
                    {JSON.parse(item.filters).record_type && 
                    <div className="text-gray-600 text-xs flex items-center m-2"> 
                        <span className="font-medium capitalize flex items-center gap-1 my-1">Record Type: {JSON.parse(item.filters).record_type.map(type => type.replace('_', ' ')).map(type => <span className='flex items-center px-1 py-1 rounded-full bg-white'>{type}</span>)}
                        </span>
                    </div>
                    }
                    {JSON.parse(item.filters).conversation_ids && 
                      <div className="text-gray-600 text-xs flex items-center m-2"> 
                          <span className="font-medium capitalize flex items-center gap-1 my-1">Conversation ID: <span className='flex items-center flex-wrap gap-1'>{JSON.parse(item.filters).conversation_ids.slice(0, 5).map(id => <span className='flex items-center px-1 py-1 rounded-full bg-white mr-1'>{id}</span>)}{JSON.parse(item.filters).conversation_ids.length > 5 && <span className='flex items-center px-1 py-1'> and {JSON.parse(item.filters).conversation_ids.length - 5} more</span>}</span>
                          </span>
                      </div>
                    }
                    {JSON.parse(item.filters).customer_ids && 
                      <div className="text-gray-600 text-xs flex items-center m-2"> 
                          <span className="font-medium capitalize flex items-center gap-1 my-1">Customer ID: <span className='flex items-center flex-wrap gap-1'>{JSON.parse(item.filters).customer_ids.slice(0, 5).map(id => <span className='flex items-center px-1 py-1 rounded-full bg-white mr-1'>{id}</span>)}{JSON.parse(item.filters).customer_ids.length > 5 && <span className='flex items-center px-1 py-1'> and {JSON.parse(item.filters).customer_ids.length - 5} more</span>}</span>
                          </span>
                      </div>
                    }
                    </div>}
                  </div>}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default StatusPanel;